import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  ansprechpartnerDisplayName,
  formatObjektadresseLine,
  getGeschaeftspartner,
  listGeschaeftspartnerAnsprechpartner,
  listGeschaeftspartnerObjektadressen,
  type Geschaeftspartner,
  type GeschaeftspartnerTyp,
  type KontaktAnsprechpartner,
  type Objektadresse,
} from "@/lib/kontakte";

export interface GeschaeftspartnerInfoModalProps {
  open: boolean;
  geschaeftspartnerId: number | null;
  typ?: GeschaeftspartnerTyp;
  onClose: () => void;
}

export function GeschaeftspartnerInfoModal({
  open,
  geschaeftspartnerId,
  typ = "lieferant",
  onClose,
}: GeschaeftspartnerInfoModalProps) {
  const navigate = useNavigate();
  const [gp, setGp] = useState<Geschaeftspartner | null>(null);
  const [ansprechpartner, setAnsprechpartner] = useState<KontaktAnsprechpartner[]>([]);
  const [objektadressen, setObjektadressen] = useState<Objektadresse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      const [gpRow, apRows, addresses] = await Promise.all([
        getGeschaeftspartner(id),
        listGeschaeftspartnerAnsprechpartner(id),
        listGeschaeftspartnerObjektadressen(id).catch(() => []),
      ]);
      setGp(gpRow);
      setAnsprechpartner(apRows);
      setObjektadressen(addresses || []);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Kontaktdaten konnten nicht geladen werden.",
      );
      setGp(null);
      setAnsprechpartner([]);
      setObjektadressen([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && geschaeftspartnerId != null) {
      setGp(null);
      setAnsprechpartner([]);
      setObjektadressen([]);
      void reload(geschaeftspartnerId);
    }
  }, [open, geschaeftspartnerId, reload]);

  const detailPath =
    typ === "kunde" ? `/kontakte/kunden/${gp?.id}` : `/kontakte/lieferanten/${gp?.id}`;
  const typLabel = typ === "kunde" ? "Kunde" : "Lieferant";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={gp?.firmenname || typLabel}
      description={gp ? `${typLabel} aus Kontakten` : undefined}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Schließen
          </Button>
          {gp ? (
            <Button
              onClick={() => {
                onClose();
                navigate(detailPath);
              }}
            >
              In Kontakten öffnen
            </Button>
          ) : null}
        </>
      }
    >
      {error ? (
        <Alert variant="error" title="Fehler">
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : gp ? (
        <div className="space-y-5">
          <section>
            <h3 className="text-sm font-semibold text-label">Stammdaten</h3>
            <dl className="mt-2 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              {gp.partnerart === "privatperson" ? (
                <>
                  <InfoRow label="Art" value="Privatperson" />
                  <InfoRow label="Anrede" value={gp.anrede} />
                  <InfoRow label="Vorname" value={gp.vorname} />
                  <InfoRow label="Nachname" value={gp.nachname} />
                </>
              ) : (
                <InfoRow label="Firmenname" value={gp.firmenname} />
              )}
              <InfoRow label="E-Mail" value={gp.email} />
              <InfoRow label="Telefon" value={gp.telefon} />
              <InfoRow
                label="Adresse"
                value={
                  [
                    gp.adresszeile1,
                    gp.adresszeile2,
                    gp.strasse,
                    [gp.plz, gp.ort].filter(Boolean).join(" "),
                  ]
                    .filter(Boolean)
                    .join(", ") || null
                }
              />
              {gp.hero_contact_id ? (
                <InfoRow label="Hero-ID" value={String(gp.hero_contact_id)} mono />
              ) : null}
            </dl>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-label">Notizen</h3>
            {gp.notizen?.trim() ? (
              <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-label">
                {gp.notizen}
              </p>
            ) : (
              <p className="mt-2 text-sm text-label-secondary">Keine Notizen hinterlegt.</p>
            )}
          </section>

          <section>
            <h3 className="text-sm font-semibold text-label">Objektadressen</h3>
            {objektadressen.length === 0 ? (
              <p className="mt-2 text-sm text-label-secondary">Keine Objektadressen hinterlegt.</p>
            ) : (
              <ul className="mt-2 divide-y divide-separator rounded-ios border border-separator">
                {objektadressen.map((addr) => (
                  <li
                    key={addr.ort_id != null ? `ort-${addr.ort_id}` : `addr-${addr.address_id}`}
                    className="px-3 py-2.5 text-sm"
                  >
                    <div className="font-medium text-label">{addr.title || "—"}</div>
                    <div className="mt-0.5 text-label-secondary">
                      {formatObjektadresseLine(addr)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h3 className="text-sm font-semibold text-label">Ansprechpartner</h3>
            {ansprechpartner.length === 0 ? (
              <p className="mt-2 text-sm text-label-secondary">Keine Ansprechpartner hinterlegt.</p>
            ) : (
              <ul className="mt-2 divide-y divide-separator rounded-ios border border-separator">
                {ansprechpartner.map((ap) => (
                  <li key={ap.id} className="px-3 py-2.5 text-sm">
                    <div className="font-medium text-label">{ansprechpartnerDisplayName(ap)}</div>
                    {ap.funktion?.trim() ? (
                      <div className="mt-0.5 text-label-secondary">{ap.funktion}</div>
                    ) : null}
                    <div className="mt-0.5 text-label-secondary">
                      {[ap.telefonnummer, ap.mobiltelefon, ap.mail].filter(Boolean).join(" · ") ||
                        "—"}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      ) : !loading && !error ? (
        <p className="text-sm text-label-secondary">Keine Kontaktdaten gefunden.</p>
      ) : null}
    </Dialog>
  );
}

function InfoRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  return (
    <div>
      <dt className="text-label-tertiary">{label}</dt>
      <dd className={mono ? "font-mono text-xs text-label" : "text-label"}>{value || "—"}</dd>
    </div>
  );
}
